import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, Play, Home, LogOut, Radio, Award, Users, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function ScorerDashboard({ user, setUser }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showTossDialog, setShowTossDialog] = useState(false);
  const [showPlayingXIDialog, setShowPlayingXIDialog] = useState(false);
  const [tossWinner, setTossWinner] = useState('');
  const [tossDecision, setTossDecision] = useState('');
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [currentTeamForXI, setCurrentTeamForXI] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/matches/assigned`);
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {}
    setUser(null);
    navigate('/');
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEE, MMM d • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const handleStartMatch = async (match) => {
    setSelectedMatch(match);
    
    // Check if toss is done
    if (!match.toss_winner_id) {
      setShowTossDialog(true);
    } else if (!match.playing_xi_a || !match.playing_xi_b) {
      // Need to set playing XI
      await loadPlayersForXI(match, match.team_a_id);
    } else {
      // Everything ready, start match
      await startMatch(match.match_id);
    }
  };

  const loadPlayersForXI = async (match, teamId) => {
    try {
      const response = await axios.get(`${API}/players`, { params: { team_id: teamId } });
      setPlayers(response.data);
      setCurrentTeamForXI(teamId === match.team_a_id ? match.team_a : match.team_b);
      setSelectedPlayers([]);
      setShowPlayingXIDialog(true);
    } catch (error) {
      toast.error('Failed to load players');
    }
  };

  const handleTossSubmit = async () => {
    if (!tossWinner || !tossDecision) {
      toast.error('Select toss winner and decision');
      return;
    }

    try {
      await axios.post(`${API}/matches/${selectedMatch.match_id}/toss`, {
        winner_team_id: tossWinner,
        decision: tossDecision
      });
      toast.success('Toss recorded');
      setShowTossDialog(false);
      
      // Now set playing XI
      await loadPlayersForXI(selectedMatch, selectedMatch.team_a_id);
      fetchData();
    } catch (error) {
      toast.error('Failed to record toss');
    }
  };

  const handlePlayingXISubmit = async () => {
    if (selectedPlayers.length !== 11) {
      toast.error('Select exactly 11 players');
      return;
    }

    try {
      await axios.post(`${API}/matches/${selectedMatch.match_id}/playing-xi`, {
        team_id: currentTeamForXI.team_id,
        player_ids: selectedPlayers
      });
      toast.success('Playing XI set');
      setShowPlayingXIDialog(false);

      // Check if we need XI for second team
      const updatedMatch = await axios.get(`${API}/matches/${selectedMatch.match_id}`);
      
      if (!updatedMatch.data.playing_xi_a && currentTeamForXI.team_id !== selectedMatch.team_a_id) {
        await loadPlayersForXI(selectedMatch, selectedMatch.team_a_id);
      } else if (!updatedMatch.data.playing_xi_b && currentTeamForXI.team_id !== selectedMatch.team_b_id) {
        await loadPlayersForXI(selectedMatch, selectedMatch.team_b_id);
      } else {
        // Both XIs set, start match
        await startMatch(selectedMatch.match_id);
      }
    } catch (error) {
      toast.error('Failed to set Playing XI');
    }
  };

  const startMatch = async (matchId) => {
    try {
      await axios.post(`${API}/matches/${matchId}/start`);
      toast.success('Match started!');
      navigate(`/scorer/match/${matchId}/scoring`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start match');
    }
  };

  const togglePlayer = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    } else if (selectedPlayers.length < 11) {
      setSelectedPlayers([...selectedPlayers, playerId]);
    }
  };

  const scheduledMatches = matches.filter(m => m.status === 'scheduled' || m.status === 'toss');
  const liveMatches = matches.filter(m => m.status === 'live');
  const completedMatches = matches.filter(m => m.status === 'completed');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-blue-600 text-white sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <span className="font-bold">Scorer</span>
                <span className="block text-xs text-white/70">Dashboard</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  <Home className="w-4 h-4" />
                </Button>
              </Link>
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.profile_image_url} />
                <AvatarFallback className="bg-white/20 text-white text-sm">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">My Matches</h1>
          <p className="text-slate-500">Matches assigned to you</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-40 rounded-2xl"></div>)}
          </div>
        ) : matches.length === 0 ? (
          <div className="empty-state bg-white rounded-2xl">
            <div className="empty-state-icon">
              <Award className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-900">No assigned matches</h3>
            <p className="text-slate-500 text-sm mt-1">Contact admin to get matches assigned</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Live Matches */}
            {liveMatches.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="live-dot"></div>
                  <h2 className="font-semibold text-slate-900">Live</h2>
                </div>
                <div className="space-y-3">
                  {liveMatches.map((match) => (
                    <Card key={match.match_id} className="card-base card-live">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Badge className="badge-live">LIVE</Badge>
                          <span className="text-sm text-slate-500">{match.overs} overs</span>
                        </div>
                        <div className="text-center py-2">
                          <p className="font-semibold">{match.team_a?.name}</p>
                          <p className="text-slate-400 text-sm my-1">vs</p>
                          <p className="font-semibold">{match.team_b?.name}</p>
                        </div>
                        <Link to={`/scorer/match/${match.match_id}/scoring`}>
                          <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 rounded-xl h-12">
                            <Radio className="w-4 h-4 mr-2" /> Continue Scoring
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled Matches */}
            {scheduledMatches.length > 0 && (
              <div>
                <h2 className="font-semibold text-slate-900 mb-4">Upcoming</h2>
                <div className="space-y-3">
                  {scheduledMatches.map((match) => (
                    <Card key={match.match_id} className="card-base">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Badge className="badge-scheduled">
                            {match.status === 'toss' ? 'TOSS DONE' : 'SCHEDULED'}
                          </Badge>
                          <span className="text-sm text-slate-500">{match.overs} overs</span>
                        </div>
                        <div className="text-center py-2">
                          <p className="font-semibold">{match.team_a?.name}</p>
                          <p className="text-slate-400 text-sm my-1">vs</p>
                          <p className="font-semibold">{match.team_b?.name}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 my-3">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDateTime(match.match_date_time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {match.ground}
                          </span>
                        </div>
                        <Button 
                          onClick={() => handleStartMatch(match)}
                          className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl h-12"
                          data-testid={`start-match-${match.match_id}`}
                        >
                          <Play className="w-4 h-4 mr-2" /> 
                          {match.status === 'toss' ? 'Set Playing XI' : 'Start Match'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {completedMatches.length > 0 && (
              <div>
                <h2 className="font-semibold text-slate-900 mb-4">Completed</h2>
                <div className="space-y-3">
                  {completedMatches.map((match) => (
                    <Card key={match.match_id} className="card-base opacity-60">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Badge className="badge-completed mb-2">COMPLETED</Badge>
                            <p className="font-medium">{match.team_a?.name} vs {match.team_b?.name}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Toss Dialog */}
      <Dialog open={showTossDialog} onOpenChange={setShowTossDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Toss</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Toss Winner</label>
              <Select value={tossWinner} onValueChange={setTossWinner}>
                <SelectTrigger>
                  <SelectValue placeholder="Select winner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={selectedMatch?.team_a_id}>{selectedMatch?.team_a?.name}</SelectItem>
                  <SelectItem value={selectedMatch?.team_b_id}>{selectedMatch?.team_b?.name}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Elected to</label>
              <Select value={tossDecision} onValueChange={setTossDecision}>
                <SelectTrigger>
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bat">Bat</SelectItem>
                  <SelectItem value="bowl">Bowl</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleTossSubmit} className="w-full bg-blue-600 hover:bg-blue-700">
            Confirm Toss
          </Button>
        </DialogContent>
      </Dialog>

      {/* Playing XI Dialog */}
      <Dialog open={showPlayingXIDialog} onOpenChange={setShowPlayingXIDialog}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Playing XI - {currentTeamForXI?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">
            Select 11 players ({selectedPlayers.length}/11)
          </p>
          <div className="space-y-2">
            {players.map((player) => (
              <div
                key={player.player_id}
                onClick={() => togglePlayer(player.player_id)}
                className={`p-3 rounded-xl border cursor-pointer flex items-center justify-between ${
                  selectedPlayers.includes(player.player_id)
                    ? 'bg-blue-50 border-blue-500'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div>
                  <p className="font-medium">{player.name}</p>
                  <p className="text-xs text-slate-500">{player.player_role}</p>
                </div>
                {selectedPlayers.includes(player.player_id) && (
                  <Check className="w-5 h-5 text-blue-600" />
                )}
              </div>
            ))}
          </div>
          <Button 
            onClick={handlePlayingXISubmit} 
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
            disabled={selectedPlayers.length !== 11}
          >
            Confirm Playing XI
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
